import { CSRF_HEADER, ProblemSchema, type Problem } from '@deli/contracts';
import type * as z from 'zod';

/** A non-2xx answer from the API, with its problem details when the body is one. */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly problem?: Problem,
  ) {
    super(problem?.detail ?? problem?.title ?? `HTTP ${status}`);
    this.name = 'ApiError';
  }

  /** Field-level messages keyed by path (from validation problems). */
  get fieldErrors(): Record<string, string> {
    return Object.fromEntries((this.problem?.errors ?? []).map((e) => [e.path, e.message]));
  }
}

export const isUnauthorized = (error: unknown): boolean =>
  error instanceof ApiError && error.status === 401;

export interface GetOptions {
  /** Non-2xx statuses whose body is still a valid answer (e.g. 503 from /ready). */
  acceptStatuses?: readonly number[];
  signal?: AbortSignal;
}

async function parse<T extends z.ZodType>(
  response: Response,
  schema: T | null,
  acceptStatuses: readonly number[] = [],
): Promise<z.infer<T>> {
  const text = await response.text();
  const body: unknown = text ? safeJson(text) : undefined;
  if (!response.ok && !acceptStatuses.includes(response.status)) {
    const problem = ProblemSchema.safeParse(body);
    throw new ApiError(response.status, problem.success ? problem.data : undefined);
  }
  return (schema ? schema.parse(body) : undefined) as z.infer<T>;
}

function safeJson(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return undefined;
  }
}

/**
 * Typed GET: the response is validated against the same zod contract the API is built from,
 * so a contract drift fails loudly instead of rendering wrong data.
 */
export async function getJson<T extends z.ZodType>(
  path: string,
  schema: T,
  options: GetOptions = {},
): Promise<z.infer<T>> {
  const response = await fetch(path, {
    headers: { accept: 'application/json' },
    credentials: 'same-origin',
    signal: options.signal,
  });
  return parse(response, schema, options.acceptStatuses);
}

/**
 * POST / PUT / PATCH / DELETE with a JSON body (or none). Always sends the CSRF header the API requires on unsafe
 * requests. Pass `null` as schema for empty (204) answers.
 */
export async function sendJson<T extends z.ZodType>(
  method: 'POST' | 'PUT' | 'PATCH' | 'DELETE',
  path: string,
  body: unknown,
  schema: T | null,
): Promise<z.infer<T>> {
  const response = await fetch(path, {
    method,
    headers: {
      accept: 'application/json',
      'content-type': 'application/json',
      [CSRF_HEADER]: '1',
    },
    credentials: 'same-origin',
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  return parse(response, schema);
}

/** Uploads a file as the raw request body (its own content type), with the CSRF header. */
export async function sendFile<T extends z.ZodType>(
  method: 'POST' | 'PUT',
  path: string,
  file: Blob,
  schema: T,
): Promise<z.infer<T>> {
  const response = await fetch(path, {
    method,
    headers: {
      accept: 'application/json',
      'content-type': file.type || 'application/octet-stream',
      [CSRF_HEADER]: '1',
    },
    credentials: 'same-origin',
    body: file,
  });
  return parse(response, schema);
}
